import { FormEvent, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { Dialog, DialogActions, DialogContent, DialogTitle, Pagination, TextField } from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import axios from 'lib/axios'
import { MainLayout } from 'views/layouts'
import { BackButton } from 'views/components/UI/Buttons'
import { useNotification } from 'lib/context/notification.context'
import getOneCompany from 'services/companies/getOneCompany'
import updateUser from 'services/companies/updateUser'
import { CompanyUser, CompanyUsersFilter, listCompanyUsers } from 'services/companies/listCompanyUsers'
import { superAdminRoutes } from 'router/routes'
import {
  FilterButton,
  FilterGroup,
  Filters,
  PageCard,
  PageHeader,
  ProcessFilter,
  RowActions,
  SearchControl,
  SearchForm,
  Status,
  TableFooter,
  TableFrame,
  UsersTable,
} from './styled'

interface EditValues {
  firstName: string
  lastName: string
  document: string
  email: string
}

interface CompanyProcess {
  process_id: number
  process_name: string
}

const roleLabels: Record<string, string> = {
  user: 'Usuario',
  special_admin: 'Administrador líder',
  super_admin: 'Superadministrador',
  platform_admin: 'Administrador de plataforma',
  super_platform_admin: 'Administrador líder de plataforma',
  partner: 'Aliado',
}

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const visibleDocument = (document: string | null) => (document && !UUID_REGEX.test(document) ? document : '')

const UsersByCompany = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { getError, getSuccess } = useNotification()
  const role = useSelector((state: any) => state.auth.role)
  const locationState = location.state as { companyId?: number; companyName?: string } | null
  const companyId = Number(locationState?.companyId || new URLSearchParams(location.search).get('companyId'))
  const [companyName, setCompanyName] = useState(locationState?.companyName || '')
  const [users, setUsers] = useState<CompanyUser[]>([])
  const [processes, setProcesses] = useState<CompanyProcess[]>([])
  const [filter, setFilter] = useState<CompanyUsersFilter>('all')
  const [processId, setProcessId] = useState<number | 'none' | undefined>()
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(0)
  const [totalResults, setTotalResults] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [reload, setReload] = useState(0)
  const [editingUser, setEditingUser] = useState<CompanyUser | null>(null)
  const [editValues, setEditValues] = useState<EditValues>({ firstName: '', lastName: '', document: '', email: '' })
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!companyId || companyName) return
    getOneCompany(companyId).then((response) => {
      if (response?.data?.name) setCompanyName(response.data.name)
    })
  }, [companyId, companyName])

  useEffect(() => {
    if (!companyId) return
    axios
      .get(`/company/process/${companyId}`)
      .then((response) => setProcesses(response.data.data.processes || []))
      .catch(() => setProcesses([]))
  }, [companyId])

  useEffect(() => {
    if (!companyId) {
      setLoading(false)
      setError('No se indicó una compañía.')
      return
    }
    let cancelled = false
    setLoading(true)
    setError('')
    listCompanyUsers(companyId, page, filter, processId, search)
      .then((data) => {
        if (cancelled) return
        setUsers(data.users)
        setTotalPages(data.total_pages)
        setTotalResults(data.total_results)
      })
      .catch(() => {
        if (!cancelled) setError('No fue posible consultar los usuarios de esta compañía.')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [companyId, page, filter, processId, search, reload])

  const showFilter = (value: CompanyUsersFilter) => {
    setFilter(value)
    setPage(1)
  }

  const showProcess = (value: string) => {
    setProcessId(value === 'all' ? undefined : value === 'none' ? 'none' : Number(value))
    setPage(1)
  }

  const searchUsers = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setPage(1)
    setSearch(searchInput.trim())
  }

  const viewEvaluations = (user: CompanyUser) => {
    navigate(superAdminRoutes.InfoEvaluatedUserByCompany, {
      state: {
        userData: {
          ...user,
          user_name: `${user.first_name} ${user.last_name}`,
          companyId,
        },
      },
    })
  }

  const edit = (user: CompanyUser) => {
    setEditingUser(user)
    setEditValues({
      firstName: user.first_name,
      lastName: user.last_name,
      document: visibleDocument(user.document),
      email: user.email || '',
    })
  }

  const save = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!editingUser || saving) return
    setSaving(true)
    const response = await updateUser(
      editingUser.id,
      editingUser.person_id,
      editValues.firstName.trim(),
      editValues.lastName.trim(),
      editValues.document.trim(),
      editValues.email.trim(),
      companyId
    )
    setSaving(false)
    if (typeof response === 'string') {
      getError('El correo o la cédula ya están registrados.')
    } else if ('error' in response && response.error) {
      getError(response.message)
    } else if (response.status === 200) {
      setEditingUser(null)
      setReload((value) => value + 1)
      getSuccess('Usuario actualizado correctamente.')
    }
  }

  const canEdit = role === 'special_admin' || role === 'super_admin'

  return (
    <MainLayout>
      <PageCard>
        <PageHeader>
          <div>
            <h2>Usuarios{companyName ? ` - ${companyName}` : ''}</h2>
            <p>Personas y administradores asociados a la compañía.</p>
          </div>
        </PageHeader>

        <Filters>
          <FilterGroup role="group" aria-label="Filtrar usuarios">
            {(
              [
                ['all', 'Todos'],
                ['evaluated', 'Evaluados'],
                ['not_evaluated', 'No evaluados'],
                ['administrators', 'Administradores'],
                ['leader_administrators', 'Administradores líderes'],
              ] as Array<[CompanyUsersFilter, string]>
            ).map(([value, label]) => (
              <FilterButton
                key={value}
                type="button"
                selected={filter === value}
                aria-pressed={filter === value}
                onClick={() => showFilter(value)}
              >
                {label}
              </FilterButton>
            ))}
          </FilterGroup>
          <ProcessFilter>
            Proceso
            <select value={processId ?? 'all'} onChange={(event) => showProcess(event.target.value)}>
              <option value="all">Todos los procesos</option>
              <option value="none">Sin proceso</option>
              {processes.map((process) => (
                <option key={process.process_id} value={process.process_id}>
                  {process.process_name}
                </option>
              ))}
            </select>
          </ProcessFilter>
          <SearchControl>
            <div>
              <span>Buscar</span>
              {search && (
                <button
                  type="button"
                  onClick={() => {
                    setSearchInput('')
                    setSearch('')
                    setPage(1)
                  }}
                >
                  Limpiar búsqueda
                </button>
              )}
            </div>
            <SearchForm onSubmit={searchUsers}>
              <input
                aria-label="Buscar por nombre, cédula, correo o requisición"
                placeholder="Nombre, cédula, correo o requisición"
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
              />
              <button type="submit" aria-label="Buscar usuarios">
                <SearchIcon />
              </button>
            </SearchForm>
          </SearchControl>
        </Filters>

        {error && (
          <p role="alert">
            {error}{' '}
            <button type="button" onClick={() => setReload((value) => value + 1)}>
              Reintentar
            </button>
          </p>
        )}
        <TableFrame>
          <UsersTable>
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Rol</th>
                <th>Cédula</th>
                <th>Correo</th>
                <th>Requisición</th>
                <th>Cuenta</th>
                <th>Evaluación</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {!loading &&
                !error &&
                users.map((user) => (
                  <tr key={user.id}>
                    <td data-label="Nombre">
                      {user.first_name} {user.last_name}
                    </td>
                    <td data-label="Rol">{roleLabels[user.role] || user.role}</td>
                    <td data-label="Cédula">{visibleDocument(user.document) || 'Sin cédula'}</td>
                    <td data-label="Correo">{user.email || '—'}</td>
                    <td data-label="Requisición">{user.requisition_code || '—'}</td>
                    <td data-label="Cuenta">
                      <Status positive={user.active}>{user.active ? 'Activa' : 'Sin activar'}</Status>
                    </td>
                    <td data-label="Evaluación">
                      <Status positive={user.evaluated}>{user.evaluated ? 'Evaluado' : 'No evaluado'}</Status>
                    </td>
                    <td data-label="Acciones">
                      <RowActions>
                        {user.process_id && (
                          <button type="button" onClick={() => viewEvaluations(user)}>
                            Ver
                          </button>
                        )}
                        {canEdit && user.role === 'user' && (
                          <button type="button" onClick={() => edit(user)}>
                            Editar
                          </button>
                        )}
                        {!user.process_id && (!canEdit || user.role !== 'user') && '—'}
                      </RowActions>
                    </td>
                  </tr>
                ))}
              {!loading && !error && users.length === 0 && (
                <tr>
                  <td colSpan={8}>No hay usuarios con estos filtros.</td>
                </tr>
              )}
              {loading && (
                <tr>
                  <td colSpan={8}>Cargando usuarios...</td>
                </tr>
              )}
            </tbody>
          </UsersTable>
        </TableFrame>
        <TableFooter>
          <span>{totalResults} usuarios</span>
          {totalPages > 1 && (
            <Pagination count={totalPages} page={page} onChange={(_, value) => setPage(value)} color="primary" />
          )}
        </TableFooter>
      </PageCard>
      <BackButton onClick={() => navigate(-1)} />

      <Dialog open={!!editingUser} onClose={() => !saving && setEditingUser(null)} fullWidth maxWidth="sm">
        <form onSubmit={save}>
          <DialogTitle>Editar usuario</DialogTitle>
          <DialogContent sx={{ display: 'grid', gap: 2, paddingTop: '12px !important' }}>
            <TextField
              label="Nombre"
              required
              value={editValues.firstName}
              onChange={(event) => setEditValues({ ...editValues, firstName: event.target.value })}
            />
            <TextField
              label="Apellido"
              required
              value={editValues.lastName}
              onChange={(event) => setEditValues({ ...editValues, lastName: event.target.value })}
            />
            <TextField
              label="Cédula"
              helperText={editingUser && !visibleDocument(editingUser.document) ? 'Esta cuenta aún no tiene una cédula registrada.' : ''}
              required
              value={editValues.document}
              onChange={(event) => setEditValues({ ...editValues, document: event.target.value })}
            />
            <TextField
              label="Correo"
              type="email"
              required
              value={editValues.email}
              onChange={(event) => setEditValues({ ...editValues, email: event.target.value })}
            />
          </DialogContent>
          <DialogActions>
            <button type="button" disabled={saving} onClick={() => setEditingUser(null)}>
              Cancelar
            </button>
            <button type="submit" disabled={saving}>
              {saving ? 'Guardando...' : 'Guardar'}
            </button>
          </DialogActions>
        </form>
      </Dialog>
    </MainLayout>
  )
}

export default UsersByCompany
